"use client";

// Test de saut d'unité (abonnés) : prouver qu'on maîtrise l'unité COURANTE
// pour la valider d'un coup. Une question par leçon (tirage serveur), au plus
// une faute, AUCUNE aide (ni indice, ni explication, ni re-essai — le pattern
// légendaire), une tentative par jour. Le verdict affiché est celui du
// SERVEUR : la correction locale ne sert qu'au feedback immédiat. Overlay
// plein écran monté par RepsPathClient, même patron que la Rafale.
//
// Habillage : la GÉOMÉTRIE du player de leçon (même barre du haut max-w-5xl,
// même carte encadrée pleine page où le verdict colore le cadre, même pied en
// grille 3 colonnes avec LE bouton d'action au centre, même bulle BeBot de
// verdict en bas à gauche) pour que le test se vive « comme les autres
// unités » — relevé légendaire par-dessus : progression et étoiles-vies OR,
// gates à CTA doré à facettes, et la voix de BeBot sur les verdicts (mêmes
// clés fermées que le player). Pas de chat ici : c'est un test.
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslations, useLocale } from "next-intl";
import Image from "next/image";
import { useSession } from "next-auth/react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import RepsExercise, {
    isWideStep,
    renderMarkdownInline,
} from "./RepsExercise";
import { gradeExercise } from "@/lib/reps/grade";
import { LEGENDARY_COLORS, LEGENDARY_FACETS } from "@/lib/reps/legendary";
import { useSound } from "@/contexts/SoundContext";
import { hasRepsAssistant } from "@/lib/reps/access";

// CTA doré des gates : la recette LegendaryGate du player (facettes en
// style inline, lèvre or foncé, texte brun, majuscules).
const GOLD_CTA =
    "press-safe block w-full rounded-2xl border-b-4 border-b-[#DFAE35] py-3.5 text-center text-base font-extrabold uppercase text-[#6A3D00] active:translate-y-1 active:border-b-transparent disabled:opacity-50";
// Pastille des heros : l'or à 14 %, lisible sur blanc comme sur sombre.
const GOLD_TINT = "rgba(245,201,66,0.14)";
// Accent or lisible : l'or de base vit bien sur sombre, le brun doré profond
// porte mieux sur blanc (LEGENDARY_COLORS.deep, parité tokens mobile).
const GOLD_ACCENT_CLS = "text-[#8A6D00] dark:text-[#F5C942]";

// Voix des verdicts, mêmes clés fermées que le player (résolues et mises en
// cache côté serveur).
const CORRECT_CHEER_COUNT = 12;

export default function RepsUnitSkip({ trackSlug, unitKey, unitTitle, onClose, onPassed }) {
    const t = useTranslations();
    const locale = useLocale();
    const { data: session } = useSession();
    const {
        soundEnabled,
        ready: soundReady,
        bebotVoiceEnabled,
        bebotPlaybackVolume,
    } = useSound();

    // phase : "intro" (règles + chargement) -> "play" -> "submitting" ->
    // "passed" | "failed" ; ou "blocked" (déjà tenté aujourd'hui) / "error".
    const [phase, setPhase] = useState("intro");
    const [test, setTest] = useState(null); // { questions, total, maxWrong, unitTitle }
    const [qi, setQi] = useState(0); // index de la question courante
    const [wrong, setWrong] = useState(0);
    const [answer, setAnswer] = useState(null);
    const [canCheck, setCanCheck] = useState(false);
    const [locked, setLocked] = useState(false); // verdict affiché, en attente de Continuer
    const [lastCorrect, setLastCorrect] = useState(null);
    const [result, setResult] = useState(null); // réponse serveur du submit
    // Copy de l'écran d'erreur : le chargement et la soumission ne racontent
    // pas la même histoire (un test JOUÉ qui ne part pas n'est pas un test
    // qui ne charge pas).
    const [errorKey, setErrorKey] = useState("reps_unit_skip_error");
    // Réponses accumulées pour la re-correction serveur + leçons ratées VUES
    // (l'écran d'échec liste ce qui a réellement été joué et raté).
    const answersRef = useRef({});
    const missedRef = useRef([]);
    const passedRef = useRef(false);
    // Voix : un seul lecteur, coupé au démontage (anti « voix zombie ») et
    // par le réglage en vol. Règles du player : la 1re bonne réponse est
    // toujours dite, ensuite ~1 sur 3 et jamais deux fois de suite ; une
    // faute est toujours dite (not_quite).
    const voiceRef = useRef(null);
    const monteRef = useRef(true);
    const voiceOnRef = useRef(false);
    const firstOkSpokenRef = useRef(false);
    const lastOkSpokenRef = useRef(false);

    useEffect(() => {
        const on = Boolean(soundEnabled && bebotVoiceEnabled);
        voiceOnRef.current = on;
        // Couper le réglage ARRÊTE la phrase en cours (parité player), en
        // plus de bloquer une synthèse encore en vol.
        if (!on && voiceRef.current) {
            try {
                voiceRef.current.pause();
            } catch (e) {
                // le lecteur a pu être déjà libéré
            }
            voiceRef.current = null;
        }
    }, [soundEnabled, bebotVoiceEnabled]);

    useEffect(() => {
        monteRef.current = true;
        return () => {
            monteRef.current = false;
            if (voiceRef.current) {
                try {
                    voiceRef.current.pause();
                } catch (e) {
                    // le lecteur a pu être déjà libéré
                }
                voiceRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        let cancelled = false;
        fetch("/api/reps/unit-skip-start", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ trackSlug, unitKey, locale }),
        })
            .then(async (r) => {
                const data = await r.json().catch(() => ({}));
                if (cancelled) return;
                if (r.ok && data.blocked) {
                    setPhase("blocked");
                } else if (r.ok && Array.isArray(data.questions)) {
                    setTest(data);
                } else {
                    setPhase("error");
                }
            })
            .catch(() => {
                if (!cancelled) setPhase("error");
            });
        return () => {
            cancelled = true;
        };
    }, [trackSlug, unitKey, locale]);

    const questions = test ? test.questions : [];
    const total = test ? test.total : 0;
    const maxWrong = test ? test.maxWrong : 1;
    const q = questions[qi] || null;
    const lives = maxWrong + 1 - wrong;

    const wide = useMemo(() => (q ? isWideStep(q.step) : false), [q]);

    // Voix BeBot d'un verdict : phrase courte lue par ElevenLabs (résolue et
    // mise en cache CÔTÉ SERVEUR depuis une liste fermée, jamais de texte
    // libre). Pas de jingle dans un test : la voix part sans différé. La
    // voix est un bonus : tout échec est silencieux.
    function speakVerdict(ok) {
        if (!soundReady || !soundEnabled || !bebotVoiceEnabled) return;
        // Même palier que le chat BeBot : évite un appel voué au refus.
        if (!hasRepsAssistant(session?.user)) return;
        let verdictKey;
        if (ok) {
            const first = !firstOkSpokenRef.current;
            firstOkSpokenRef.current = true;
            const react =
                first || (Math.random() < 0.4 && !lastOkSpokenRef.current);
            lastOkSpokenRef.current = react;
            if (!react) return;
            const n = Math.floor(Math.random() * CORRECT_CHEER_COUNT) + 1;
            verdictKey = `correct_cheer_${String(n).padStart(2, "0")}`;
        } else {
            // Une faute ne réarme PAS le cheer (parité player : seul le
            // branch ok touche l'anti-répétition).
            verdictKey = "not_quite";
        }
        fetch("/api/reps/assistant/voice", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ locale, verdictKey }),
        })
            .then((r) => (r.ok ? r.json() : null))
            .then((data) => {
                if (!data || !data.url) return;
                // Voix coupée pendant la synthèse, ou test refermé : silence.
                if (!monteRef.current || !voiceOnRef.current) return;
                try {
                    if (voiceRef.current) voiceRef.current.pause();
                    const a = new Audio(data.url);
                    a.volume = bebotPlaybackVolume;
                    voiceRef.current = a;
                    a.play().catch(() => {});
                } catch (e) {
                    // la voix est un bonus
                }
            })
            .catch(() => {});
    }

    function submitTest(finalAnswers, missed) {
        setPhase("submitting");
        const clientEventId = `unit-skip-${unitKey}-${Date.now()}-${Math.random()
            .toString(36)
            .slice(2, 10)}`;
        fetch("/api/reps/unit-skip-submit", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                unitKey,
                answers: finalAnswers,
                clientEventId,
                clientTimestamp: new Date().toISOString(),
            }),
        })
            .then(async (r) => {
                const data = await r.json().catch(() => ({}));
                if (!r.ok) {
                    setErrorKey("reps_unit_skip_submit_error");
                    setPhase("error");
                    return;
                }
                setResult({ ...data, missed });
                if (data.passed) {
                    passedRef.current = true;
                    if (onPassed) onPassed();
                    setPhase("passed");
                } else {
                    setPhase("failed");
                }
            })
            .catch(() => {
                setErrorKey("reps_unit_skip_submit_error");
                setPhase("error");
            });
    }

    function handleCheck() {
        if (!q) return;
        if (!locked) {
            if (!canCheck) return;
            const ok = gradeExercise(q.step, answer);
            answersRef.current[q.position] = answer;
            if (!ok) {
                missedRef.current = [
                    ...missedRef.current,
                    { code: q.lessonSlug, title: q.lessonTitle },
                ];
            }
            setLastCorrect(ok);
            setLocked(true);
            if (!ok) setWrong((w) => w + 1);
            speakVerdict(ok);
            return;
        }
        // Continuer : question suivante, ou fin (verdict scellé dès que les
        // fautes dépassent le droit à l'erreur — inutile de jouer le reste).
        const nextWrong = wrong;
        const finalAnswers = Object.keys(answersRef.current).map((p) => ({
            position: Number(p),
            answer: answersRef.current[p],
        }));
        if (nextWrong > maxWrong || qi + 1 >= total) {
            submitTest(finalAnswers, missedRef.current);
            return;
        }
        setQi((i) => i + 1);
        setAnswer(null);
        setCanCheck(false);
        setLocked(false);
        setLastCorrect(null);
    }

    function handleChange(a, complete) {
        setAnswer(a);
        setCanCheck(complete);
    }

    // Entrée = le bouton d'action, comme dans le player (le kbd ↵ du bouton
    // l'annonce). Dans un champ de saisie, Entrée reste à l'exercice tant que
    // le verdict n'est pas verrouillé (aller à la ligne, valider un blank...).
    useEffect(() => {
        function onKeyDown(e) {
            if (e.key !== "Enter" || phase !== "play") return;
            const el = document.activeElement;
            const typing =
                el &&
                (el.tagName === "INPUT" ||
                    el.tagName === "TEXTAREA" ||
                    el.isContentEditable);
            if (typing && !locked) return;
            if (!locked && !canCheck) return;
            e.preventDefault();
            handleCheck();
        }
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    });

    const missedShown =
        result && Array.isArray(result.missed) ? result.missed : [];

    // Le verdict est porté par le CADRE de la carte, copie du player (façon
    // Brilliant) : bordure + halo doux, vert = juste, rouge = raté. La carte
    // n'existe qu'à partir de sm, comme dans le player.
    const cardVerdictCls = locked
        ? lastCorrect
            ? "sm:border-emerald-400 sm:shadow-[0_0_0_1px_#34d399,0_0_28px_-6px_rgba(16,185,129,0.45)] sm:dark:border-emerald-400/70 sm:dark:shadow-[0_0_0_1px_rgba(52,211,153,0.55),0_0_28px_-6px_rgba(16,185,129,0.35)]"
            : "sm:border-red-300 sm:shadow-[0_0_0_1px_#fca5a5,0_0_28px_-6px_rgba(239,68,68,0.35)] sm:dark:border-red-400/60 sm:dark:shadow-[0_0_0_1px_rgba(248,113,113,0.5),0_0_28px_-6px_rgba(239,68,68,0.3)]"
        : "sm:border-gray-200 sm:shadow-sm sm:dark:border-white/10";

    // Le bouton d'action du jeu : la recette du player (l'or est réservé aux
    // gates, comme dans le mode légendaire).
    let btnCls;
    if (locked) {
        btnCls = lastCorrect
            ? "border-b-emerald-700 bg-emerald-500 text-white hover:bg-emerald-600"
            : "border-b-red-700 bg-red-500 text-white hover:bg-red-600";
    } else if (canCheck) {
        btnCls = "border-b-orange-700 bg-orange-500 text-white hover:bg-orange-600";
    } else {
        btnCls =
            "cursor-not-allowed bg-gray-200 text-gray-400 border-b-gray-300 dark:bg-white/10 dark:text-grayDark-500 dark:border-b-white/5";
    }

    // Remplissage or : la barre raconte le test, et reste pleine sur
    // l'écran de réussite (l'unité est passée).
    const progressWidth =
        phase === "play" && total
            ? `${(qi / total) * 100}%`
            : phase === "passed"
              ? "100%"
              : "0%";

    return (
        <div className="fixed inset-0 z-[100] flex flex-col bg-white text-gray-900 dark:bg-[#192734] dark:text-white">
            {/* Barre du haut : la géométrie du player (max-w-5xl), avec la
                progression OR et les étoiles-vies du légendaire. */}
            <div className="mx-auto flex w-full max-w-5xl items-center gap-2.5 px-3 py-3 sm:gap-4 sm:px-6 sm:py-3.5">
                <button
                    type="button"
                    aria-label={t("reps_unit_skip_close")}
                    onClick={onClose}
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-white/10 dark:hover:text-grayDark-200"
                >
                    <SolarIcon name="close-circle-linear" className="text-2xl" />
                </button>
                <div className="h-3.5 flex-1 overflow-hidden rounded-full bg-gray-200 dark:bg-white/10">
                    <div
                        className="h-full rounded-full transition-[width] duration-300 ease-out"
                        style={{
                            backgroundColor: LEGENDARY_COLORS.base,
                            width: progressWidth,
                        }}
                    />
                </div>
                {phase === "play" && (
                    <span className="flex shrink-0 items-center gap-0.5">
                        {/* Le compte reste lisible au lecteur d'écran (l'ancienne
                            pastille l'affichait en texte, les étoiles seules
                            seraient muettes). */}
                        <span className="sr-only">{lives}</span>
                        {Array.from({ length: maxWrong + 1 }, (_, i) => (
                            <SolarIcon
                                key={i}
                                name="star-bold"
                                className={`text-lg ${
                                    i < lives
                                        ? "text-[#F5C942]"
                                        : "text-gray-300 dark:text-white/20"
                                }`}
                            />
                        ))}
                    </span>
                )}
            </div>

            {phase === "intro" && (
                <div className="mx-auto flex w-full max-w-xl flex-1 flex-col justify-center px-4 pb-8 text-center">
                    <div
                        className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl"
                        style={{ background: GOLD_TINT }}
                    >
                        <SolarIcon
                            name="double-alt-arrow-down-bold"
                            className={`text-4xl ${GOLD_ACCENT_CLS}`}
                        />
                    </div>
                    <h2 className="text-2xl font-extrabold text-gray-900 dark:text-white">
                        {t("reps_unit_skip_title")}
                    </h2>
                    {/* Les règles n'annoncent JAMAIS un nombre de questions
                        inventé : on attend le vrai total du serveur. */}
                    {test ? (
                        <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-gray-500 dark:text-grayDark-400">
                            {t("reps_unit_skip_subtitle", {
                                unit: test.unitTitle || unitTitle,
                                total,
                            })}
                        </p>
                    ) : (
                        <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-gray-400">
                            …
                        </p>
                    )}
                    <div className="mx-auto mt-7 w-full max-w-xs sm:max-w-sm">
                        <button
                            type="button"
                            disabled={!test}
                            onClick={() => setPhase("play")}
                            className={GOLD_CTA}
                            style={{ background: LEGENDARY_FACETS }}
                        >
                            {test ? t("reps_unit_skip_go") : "…"}
                        </button>
                    </div>
                </div>
            )}

            {phase === "play" && q && (
                <>
                    {/* La carte du player : encadrée en permanence (desktop),
                        le test vit dedans ; plein écran nu sous sm. */}
                    <div
                        className={`flex min-h-0 min-w-0 flex-1 flex-col transition-[border-color,box-shadow] duration-300 ease-out sm:mx-3 sm:mb-3 sm:overflow-hidden sm:rounded-2xl sm:border sm:bg-white sm:dark:bg-[#22303C] ${cardVerdictCls}`}
                    >
                        <div className="min-h-0 flex-1 overflow-y-auto">
                            <div className="flex min-h-full items-center justify-center px-4 py-6">
                                <div
                                    className={`w-full min-w-0 ${
                                        wide ? "max-w-6xl" : "max-w-3xl"
                                    }`}
                                >
                                    {q.step.prompt ? (
                                        <h1 className="mx-auto mb-6 w-full max-w-[600px] whitespace-pre-line text-left text-lg font-medium leading-relaxed text-gray-800 dark:text-grayDark-100">
                                            {renderMarkdownInline(q.step.prompt)}
                                        </h1>
                                    ) : null}
                                    <RepsExercise
                                        // Contrat RepsExercise : l'exercice est
                                        // REMONTÉ à chaque question (le player
                                        // utilise key=visit) — sans cette key,
                                        // deux questions du même type partagent
                                        // leur état interne (permutation des
                                        // options, sélection fantôme).
                                        key={`us-${q.position}`}
                                        exercise={q.step}
                                        uid={`unit-skip-${unitKey}-${q.position}`}
                                        lang={
                                            /^js-/.test(q.lessonSlug || "")
                                                ? "javascript"
                                                : /^(py|p2)-/.test(q.lessonSlug || "")
                                                  ? "python"
                                                  : "html"
                                        }
                                        onChange={handleChange}
                                        disabled={locked}
                                        revealed={locked}
                                        verdict={locked ? lastCorrect : null}
                                    />
                                </div>
                            </div>
                        </div>
                        {/* Pied de carte du player : grille 3 colonnes, LE
                            bouton d'action vit seul au centre et n'est jamais
                            déplacé (pas d'Explication ni de Solution dans un
                            test — les colonnes latérales restent vides). */}
                        <div className="mx-auto grid w-full max-w-5xl grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2 px-3 py-3 sm:gap-3 sm:px-6 sm:py-3.5">
                            <div />
                            <button
                                type="button"
                                disabled={!locked && !canCheck}
                                onClick={handleCheck}
                                className={`press-safe flex min-w-[8rem] shrink-0 items-center justify-center gap-2 rounded-2xl border-b-4 px-4 py-3 text-sm font-bold transition-all duration-150 active:translate-y-1 active:border-b-transparent sm:min-w-[10rem] sm:px-7 ${btnCls}`}
                            >
                                {locked
                                    ? t("reps_unit_skip_continue")
                                    : t("reps_unit_skip_check")}
                                <kbd className="hidden rounded border border-white/40 px-1.5 py-0.5 text-[10px] font-semibold leading-none opacity-80 sm:inline">
                                    ↵
                                </kbd>
                            </button>
                            <div />
                        </div>
                    </div>
                    {/* Bulle de verdict du player (mini BeBot + phrase), en bas
                        à gauche — jamais d'explication ni de solution : un test
                        n'aide pas. */}
                    {locked && (
                        <div className="pointer-events-none fixed bottom-[86px] left-3 z-[54] flex items-end gap-2 sm:bottom-24 sm:left-5">
                            {/* <img> nu comme la bulle du player : ces SVG ne
                                passent pas par l'optimiseur d'images. */}
                            <img
                                src={
                                    lastCorrect
                                        ? "/images/bebot/coach/happy.webp"
                                        : "/images/bebot/coach/sad.webp"
                                }
                                alt="BeBot"
                                className="h-10 w-10 shrink-0 object-contain"
                            />
                            <div
                                className={`relative max-w-[calc(100vw-84px)] rounded-2xl px-4 py-3 text-sm font-semibold sm:max-w-xs ${
                                    lastCorrect
                                        ? "bg-emerald-100 text-emerald-900 dark:bg-emerald-500/20 dark:text-emerald-100"
                                        : "bg-red-100 text-red-900 dark:bg-red-500/15 dark:text-red-100"
                                }`}
                            >
                                {lastCorrect
                                    ? t("reps_unit_skip_correct")
                                    : t("reps_unit_skip_wrong")}
                            </div>
                        </div>
                    )}
                </>
            )}

            {phase === "submitting" && (
                <div className="mx-auto flex w-full max-w-xl flex-1 items-center justify-center px-4 pb-8">
                    <p className="text-sm text-gray-400">…</p>
                </div>
            )}

            {phase === "passed" && (
                <div className="mx-auto flex w-full max-w-xl flex-1 flex-col justify-center px-4 pb-8 text-center">
                    <SolarIcon
                        name="cup-star-bold"
                        className={`mx-auto text-8xl ${GOLD_ACCENT_CLS}`}
                    />
                    <h2 className="mt-6 text-2xl font-extrabold text-gray-900 dark:text-white">
                        {t("reps_unit_skip_passed_title")}
                    </h2>
                    <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-gray-500 dark:text-grayDark-400">
                        {t("reps_unit_skip_passed_sub", {
                            unit: (test && test.unitTitle) || unitTitle,
                        })}
                    </p>
                    {result && result.xpGained > 0 && (
                        <p
                            className={`mt-4 text-lg font-extrabold ${GOLD_ACCENT_CLS}`}
                        >
                            +{result.xpGained} XP
                        </p>
                    )}
                    <div className="mx-auto mt-7 w-full max-w-xs sm:max-w-sm">
                        <button
                            type="button"
                            onClick={onClose}
                            className={GOLD_CTA}
                            style={{ background: LEGENDARY_FACETS }}
                        >
                            {t("reps_unit_skip_continue")}
                        </button>
                    </div>
                </div>
            )}

            {phase === "failed" && (
                <div className="mx-auto flex w-full max-w-xl flex-1 flex-col justify-center px-4 pb-8 text-center">
                    <Image
                        src="/images/bebot/bebot-sad-1k.png"
                        alt="BeBot"
                        width={110}
                        height={110}
                        className="mx-auto h-28 w-auto"
                    />
                    <h2 className="mt-6 text-2xl font-extrabold text-gray-900 dark:text-white">
                        {t("reps_unit_skip_failed_title")}
                    </h2>
                    <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-gray-500 dark:text-grayDark-400">
                        {t("reps_unit_skip_failed_sub", {
                            required: total - maxWrong,
                            total,
                        })}
                    </p>
                    {missedShown.length > 0 && (
                        <div className="mx-auto mt-5 w-full max-w-sm rounded-2xl border border-gray-200 p-4 text-left dark:border-white/10">
                            <p className="text-xs font-bold uppercase tracking-wide text-gray-400">
                                {t("reps_unit_skip_failed_lessons")}
                            </p>
                            <ul className="mt-2 space-y-1.5">
                                {missedShown.map((m) => (
                                    <li
                                        key={m.code}
                                        className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-grayDark-200"
                                    >
                                        <SolarIcon
                                            name="book-2-bold"
                                            className={`shrink-0 text-base ${GOLD_ACCENT_CLS}`}
                                        />
                                        {m.title}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    <div className="mx-auto mt-7 w-full max-w-xs sm:max-w-sm">
                        <button
                            type="button"
                            onClick={onClose}
                            className={GOLD_CTA}
                            style={{ background: LEGENDARY_FACETS }}
                        >
                            {t("reps_unit_skip_close")}
                        </button>
                    </div>
                </div>
            )}

            {phase === "blocked" && (
                <div className="mx-auto flex w-full max-w-xl flex-1 flex-col justify-center px-4 pb-8 text-center">
                    <div
                        className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl"
                        style={{ background: GOLD_TINT }}
                    >
                        <SolarIcon
                            name="alarm-bold"
                            className={`text-4xl ${GOLD_ACCENT_CLS}`}
                        />
                    </div>
                    <h2 className="text-2xl font-extrabold text-gray-900 dark:text-white">
                        {t("reps_unit_skip_blocked_title")}
                    </h2>
                    <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-gray-500 dark:text-grayDark-400">
                        {t("reps_unit_skip_blocked_sub")}
                    </p>
                    <div className="mx-auto mt-7 w-full max-w-xs sm:max-w-sm">
                        <button
                            type="button"
                            onClick={onClose}
                            className={GOLD_CTA}
                            style={{ background: LEGENDARY_FACETS }}
                        >
                            {t("reps_unit_skip_close")}
                        </button>
                    </div>
                </div>
            )}

            {phase === "error" && (
                <div className="mx-auto flex w-full max-w-xl flex-1 flex-col justify-center px-4 pb-8 text-center">
                    <p className="text-sm text-gray-500 dark:text-grayDark-400">
                        {t(errorKey)}
                    </p>
                    <div className="mx-auto mt-6 w-full max-w-xs sm:max-w-sm">
                        <button
                            type="button"
                            onClick={onClose}
                            className={GOLD_CTA}
                            style={{ background: LEGENDARY_FACETS }}
                        >
                            {t("reps_unit_skip_close")}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
